import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BusinessPartnerNavigationComponent } from '../business-partner-navigation/business-partner-navigation.component';
import {
  BusinessPartnerNavigationData,
  BusinessPartnerQueryParams,
  BusinessPartnerNavigationEvent
} from '../business-partner-navigation/interfaces/business-partner-navigation.interfaces';
import {
  BusinessPartnerTypeEnum,
  getBusinessPartnerTypeDescription,
  getBusinessPartnerTypeIcon,
  getBusinessPartnerTypeColor
} from './business-partner-type.enum';
import { A11yModule } from "@angular/cdk/a11y";

// Type for Business Partner Data Display component only
interface BusinessPartnerData {
  _id?: string;
  id?: number;
  externalKeys?: {
    code?: string;
    _id?: string;
  };
  name?: string;
  types?: Array<{
    _id?: string;
    code?: string;
    name?: string;
    statusId?: number;
  }>;
  statusId?: number;
  personNumber?: string;
  legalCode?: string;
}

@Component({
  selector: 'app-business-partner-display',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    BusinessPartnerNavigationComponent,
    A11yModule
],
  templateUrl: './business-partner-display.component.html',
  styleUrls: ['./business-partner-display.component.scss']
})
export class BusinessPartnerDisplayComponent {
  @Input() soldTo: BusinessPartnerData | null = null;
  @Input() shipTo: BusinessPartnerData | null = null;
  @Input() displayMode: 'vertical' | 'horizontal' = 'vertical';
  @Input() showLabels: boolean = true;
  @Input() showIcons: boolean = false;
  @Input() customerType?: string; // BPT9001, BPT9002, etc.
  @Input() compact: boolean = false;

  // Navigation inputs
  @Input() enableNavigation: boolean = true;
  @Input() navigationQueryParams: BusinessPartnerQueryParams = {};
  @Input() navigationDisplayMode: 'code' | 'name' | 'both' = 'both';
  @Input() openNavigationInNewTab: boolean = true;
  @Input() showNavigationIcon: boolean = true;
  @Input() showNavigationText: boolean = false;
  @Input() navigationInteractionMode: 'both' | 'text-only' | 'icon-only' = 'icon-only';
  @Input() navigationIconPosition: 'left' | 'right' = 'right';
  @Input() customNavigationIcon: string = 'heroicons_outline:rocket-launch';
  @Input() customNavigationTooltip?: string;
  @Input() useShipTo = true;
  @Input() useSoldTo = true;

  // Navigation outputs
  @Output() onBusinessPartnerNavigate = new EventEmitter<{
    type: 'soldTo' | 'shipTo';
    data: BusinessPartnerNavigationData;
    queryParams: BusinessPartnerQueryParams;
  }>();

  get soldToCode(): string {
    return this.soldTo?.externalKeys?.code || '-';
  }

  get soldToName(): string {
    return this.soldTo?.name || '-';
  }

  get shipToCode(): string {
    return this.shipTo?.externalKeys?.code || '-';
  }

  get shipToName(): string {
    return this.shipTo?.name || '-';
  }

  get customerTypeTooltip(): string {
    return this.customerType ? getBusinessPartnerTypeDescription(this.customerType) : '';
  }

  get customerTypeIcon(): string {
    return this.customerType ? getBusinessPartnerTypeIcon(this.customerType) : '';
  }

  get customerTypeColor(): string {
    return this.customerType ? getBusinessPartnerTypeColor(this.customerType) : '#757575';
  }

  // Navigation data getters
  get soldToNavigationData(): BusinessPartnerNavigationData {
    return {
      id: this.soldTo?._id,
      code: this.soldTo?.externalKeys?.code,
      name: this.soldTo?.name,
      externalKeys: this.soldTo?.externalKeys
    };
  }

  get shipToNavigationData(): BusinessPartnerNavigationData {
    return {
      id: this.shipTo?._id,
      code: this.shipTo?.externalKeys?.code,
      name: this.shipTo?.name,
      externalKeys: this.shipTo?.externalKeys
    };
  }

  // Navigation event handlers
  handleSoldToNavigation(event: BusinessPartnerNavigationEvent): void {
    this.onBusinessPartnerNavigate.emit({
      type: 'soldTo',
      data: event.data,
      queryParams: event.queryParams
    });
  }

  handleShipToNavigation(event: BusinessPartnerNavigationEvent): void {
    this.onBusinessPartnerNavigate.emit({
      type: 'shipTo',
      data: event.data,
      queryParams: event.queryParams
    });
  }
}